/**
 * usePageViewBeacon — records the current user's screen navigation to the backend
 * activity log (POST /activity/pageview) on every route change. Feeds the Super Risk
 * Admin "User Activity" screen with precise "where did they go" history.
 *
 * Best-effort and non-blocking: a failed beacon never affects the user's browsing.
 * Call this once from an authenticated component mounted inside the Router (App.js).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { protectedResources, siteData } from "authConfig";
import useFetchWithMsal from "./useFetchWithMsal";

// Path prefix → friendly screen label. First match (longest/most specific first) wins.
const SCREEN_MAP = [
  ["/anticipate/HorizonScan", "Horizon Scan"],
  ["/anticipate/EmergingRisks", "Watchlist Risks"],
  ["/anticipate/board-paper", "Board Paper"],
  ["/anticipate/risk-advisor", "Risk Advisor"],
  ["/anticipate/signpost-library", "Signpost Library"],
  ["/anticipate/signpost-requests", "Signpost Requests"],
  ["/anticipate/configuration/ignore-domains", "Ignored Domains"],
  ["/anticipate/configuration/priority-domains", "Trusted Sources"],
  ["/appetite-settings/risk-appetite-levels", "Risk Appetite Levels"],
  ["/appetite-settings/risk-evaluation-criteria", "Risk Evaluation Criteria"],
  ["/appetite-settings/response-status", "Response Status"],
  ["/CompanySearchParameters", "Horizon Scan Collection Parameters"],
  ["/user-activity", "User Activity"],
  ["/AppetiteSetting", "Appetite Setting"],
  ["/ConfigureSizingCriteria", "Risk and Incident Sizing"],
  ["/AssuranceSizingCriteria", "Assurance Sizing"],
  ["/AssuranceProportionality", "Assurance Proportionality"],
  ["/LikelihoodCriteria", "Likelihood Criteria"],
  ["/Taxonomy", "Taxonomy"],
  ["/users", "Users"],
  ["/filestatus", "File Status"],
  ["/dataindexing", "Data Indexing"],
  ["/dashboard/Profile", "Profile Setting"],
  ["/overview", "Protect"],
  ["/dashboard", "Anticipate Dashboard"],
  ["/home", "Home"],
];

function labelForPath(pathname) {
  const p = String(pathname || "");
  const hit = SCREEN_MAP.find(([prefix]) => p.toLowerCase().startsWith(prefix.toLowerCase()));
  if (hit) return hit[1];
  // Fallback: humanise the last non-empty path segment.
  const seg = p.split("/").filter(Boolean).pop() || "Home";
  return seg
    .replace(/[-_]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function usePageViewBeacon() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname;
    if (!path || lastPath.current === path) return;
    lastPath.current = path;
    const base = protectedResources?.apiTodoList?.endpoint || "";
    if (!base) return;
    try {
      const res = executeRef.current("POST", `${base}/activity/pageview`, {
        path,
        screenLabel: labelForPath(path),
      });
      if (res && typeof res.catch === "function") res.catch(() => {});
    } catch {
      /* never disrupt navigation */
    }
  }, [location.pathname]);
}
